import { Box, Flex } from "@chakra-ui/layout";
import { Heading, Button, Text, Image } from "@chakra-ui/react";
import { BsCheck2 } from "react-icons/bs";
import { MdClear } from "react-icons/md";
import { backToWebApp, formatCurrency } from "utils/helpers";
import { useNavigate } from "react-router-dom";
import useGlobalStore from "zStore";
import { useTenant } from "context/TenantContext";
import { PUBLIC_PAGES } from "routes/pagePath";
import { ENV } from "utils/constants";
import { EVENTS, Mixpanel } from "lib/mixpanel";
import { useTranslation } from "react-i18next";
import i18n from "i18n";
import { resolvePlanLogoSrc } from "./resolvePlanLogo";

type pricing = {
  iosPlanId?: string;
  cost: string;
  save: string;
  ios: string;
  id: string;
  iosExternalPlanId?: string;
};

type AccessType = {
  textKey: string;
  isEnabled: boolean;
};

export type PlanType = {
  id: number;
  orderId: number;
  isFree: boolean;
  header: {
    logo: string;
    name: string;
    subText: string;
  };
  access: Array<AccessType>;
  price: {
    currency: string;
    duration: Partial<Record<PlanDurationKey, pricing>>;
  };
};

/** Presentation-only plan data. Pricing and free/paid status come from the API. */
export type PlanTemplate = Omit<PlanType, "isFree">;

export type DurationValue = "ANNUAL" | "SEMI_ANNUAL" | "MONTH" | "QUARTERLY";
/** Free plans are billed as UNLIMITED, which has no matching duration tab. */
export type PlanDurationKey = DurationValue | "UNLIMITED";
export type DeviceType = "ios" | "android";

export interface SubCardType {
  plan: PlanType;
  durationType: DurationValue;
  deviceType: DeviceType;
  businessId?: string | undefined | null;
  openGoldModal: () => void;
}

const SubCard = (props: SubCardType) => {
  const { t, i18n: i18nInstance } = useTranslation("subscriptions");
  const [setSub] = useGlobalStore((state) => [state.setSubscription]);
  const { primaryColor, tenant } = useTenant();
  const { plan, durationType, deviceType, businessId, openGoldModal } = props;
  const { header, access, price } = plan;
  const navigate = useNavigate();
  const isFreePlan = plan.isFree;
  const isSilver = header.name === "Silver";
  const isBestValue = header.name === "Gold";
  const isIos = deviceType === "ios";
  const durationPricing = price.duration[durationType];
  const logoSrc = resolvePlanLogoSrc(tenant.favicon, header.logo);

  if (!isFreePlan && !durationPricing) {
    return null;
  }

  const planPrice = isFreePlan
    ? "0"
    : isIos
    ? durationPricing!.ios
    : durationPricing!.cost;
  const planSaving = durationPricing?.save ?? "more";
  const planId = durationPricing?.id ?? "";
  const iosPlanId = durationPricing?.iosPlanId;
  let iosExternalPlanId = durationPricing?.iosExternalPlanId || "";
  const { duration, durationWord, subText } =
    getSubscriptionDetails(durationType);

  const handleSelection = () => {
    if (isSilver) {
      openGoldModal();
      Mixpanel.track(EVENTS.PROMPT_VIEWED);
      return;
    }
    if (isFreePlan) {
      let backToApp = PUBLIC_PAGES.ANDROID_HOME;
      if (deviceType === "ios") {
        backToApp = PUBLIC_PAGES.IOS_HOME;
        return (window.location.href = backToApp);
      }
      backToWebApp(tenant);
      return;
    }
    if (deviceType === "ios") {
      if (process.env.REACT_APP_CLIENT_ENV_QA === ENV.DEV) {
        if (iosExternalPlanId.includes(".OZE.")) {
          iosExternalPlanId = iosExternalPlanId.replace(
            ".OZE.",
            ".testflightqa."
          );
        }
      }
      const url = `/ios?planId=${iosPlanId}&externalPlanId=${iosExternalPlanId}`;
      const iosPage = url;
      return (window.location.href = iosPage);
    }

    const currentSub = {
      planName: plan.header.name,
      image: logoSrc,
      duration,
      durationWord,
      durationType,
      date: new Date(),
      currency: plan.price.currency,
      amount: planPrice,
      id: planId,
      businessId,
    };
    setSub({
      ...currentSub,
    });
    const location = window.location.href;
    localStorage.setItem("current-location", location);
    navigate("/order");
  };

  const planSubTextKey = `planSubText.${header.name}` as const;

  return (
    <Box
      bg="#F7F7F7"
      mt="8"
      mx="23px"
      border="1px solid #EEEEEE"
      boxShadow="0px 4px 32px rgba(0, 0, 0, 0.08)"
      borderRadius="16px"
      p="24px"
      overflow="hidden"
    >
      <Flex justifyContent="space-between" position="relative">
        <Flex>
          {logoSrc ? (
            <Image
              w="44px"
              h="44px"
              mr="13px"
              objectFit="cover"
              src={logoSrc}
              alt={t("planPreviewAlt")}
            />
          ) : (
            <Box w="44px" h="44px" mr="13px" borderRadius="8px" bg="gray.200" />
          )}
          <Box>
            <Heading color="black" fontSize="24px">
              {header.name}
            </Heading>

            <Text color="black" fontSize="12px">
              {t(planSubTextKey, { defaultValue: header.subText })}
            </Text>
          </Box>
        </Flex>
        {isBestValue ? <GoldBanner /> : null}
      </Flex>
      <Box mt="32px">
        {access.map((item, index) => (
          <Flex mt="8px" key={index}>
            {item.isEnabled ? (
              <BsCheck2
                color={primaryColor}
                size="13px"
                style={{ marginTop: "2px" }}
              />
            ) : (
              <MdClear
                color="#8B8CAD"
                size="13px"
                style={{ marginTop: "2px" }}
              />
            )}
            <Text
              ml="6px"
              color={item.isEnabled ? "#0A0C29" : "#73769D"}
              fontSize="12px"
            >
              {t(`features.${item.textKey}`)}
            </Text>
          </Flex>
        ))}
      </Box>

      <Box mt="24px">
        <Flex color="black" alignItems="center">
          {isFreePlan ? (
            <Heading fontSize="24px">{t("free")}</Heading>
          ) : (
            <Heading fontSize="24px">
              {formatCurrency(+planPrice, price.currency, i18nInstance.language)}
            </Heading>
          )}

          <Text mt="4px" ml="8px">
            / {isFreePlan ? t("forever") : duration}
          </Text>
        </Flex>
        {isFreePlan ? (
          <Text color="black" fontSize="12px" fontWeight={500}>
            {t("freePlanHint")}
          </Text>
        ) : (
          <Flex mt="5px">
            <Text color="primary" fontSize="12px" fontWeight={700}>
              {t("saveAmount", { amount: planSaving })}
            </Text>

            <Text color="black" ml="5px" fontSize="12px" fontWeight={500}>
              {subText}
            </Text>
          </Flex>
        )}
        <Button w="100%" mt="32px" bg="primary" h="48px" onClick={handleSelection}>
          {t("getPlan", { name: header.name })}
        </Button>
      </Box>
    </Box>
  );
};

export default SubCard;

export const GoldBanner = () => {
  const { t } = useTranslation("subscriptions");
  return (
    <Box
      bg="primary"
      transform="rotate(45deg)"
      color="#fff"
      fontWeight={600}
      textAlign="center"
      position="absolute"
      top="11px"
      right={{ base: "-67px", md: "-83px" }}
      width={{ base: "67%", md: "50%" }}
      fontSize={{ base: "14px", md: "16px" }}
    >
      {t("bestValue")}
    </Box>
  );
};

export const getSubscriptionDetails = (durationType: string) => {
  const key = durationType as "ANNUAL" | "SEMI_ANNUAL" | "MONTH" | "QUARTERLY";
  const knownKeys = ["ANNUAL", "SEMI_ANNUAL", "MONTH", "QUARTERLY"];
  const safeKey = knownKeys.includes(key) ? key : "MONTH";

  return {
    duration: i18n.t(`subscriptions:duration.${safeKey}`),
    durationWord: i18n.t(`subscriptions:durationWord.${safeKey}`),
    subText: i18n.t(`subscriptions:durationSubText.${safeKey}`),
  };
};
